import React, { useState, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useApi } from "./api";
import { useAuth } from "./auth";
const UserInfoContext = createContext({
  roles: [],
  stripeIsSetup: false,
  isAccountFullySetup: false,
});
const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;

export const useUserInfo = () => useContext(UserInfoContext);

export const UserInfoContextProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const {
    getCurrentUser,
    getUser,
    getAccount,
    getCoachSubscription,
    getUserProducts,
  } = useApi();
  const [userProfile, setUserProfile] = useState(() => {
    // Try to retrieve user info from localStorage on initial load
    try {
      const storedUserInfo = localStorage.getItem("userInfo");
      if (storedUserInfo) {
        return JSON.parse(storedUserInfo);
      }
    } catch (error) {
      console.error("Error parsing userInfo from localStorage:", error);
    }
    return {};
  });

  useEffect(() => {
    const load = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);

      let profile;
      try {
        const storedUserInfo = localStorage.getItem("userInfo");
        if (storedUserInfo) {
          profile = JSON.parse(storedUserInfo);
          setUserProfile(profile);
        }
      } catch (error) {
        console.error("Error parsing userInfo from localStorage:", error);
      }

      if (!profile) {
        const userInfo = await getCurrentUser();
        if (userInfo.coachId) {
          const coachInfo = await getUser(userInfo.coachId);
          userInfo.coachAccountId = coachInfo.stripeAccountId;
        }
        profile = userInfo;
        setUserProfile(profile);
        try {
          localStorage.setItem("userInfo", JSON.stringify(userInfo));
        } catch (error) {
          console.error("Error saving userInfo to localStorage:", error);
        }
      }

      if (!profile?.isCoach) {
        const [assignedProducts, purchasedProducts] = await Promise.all([
          getUserProducts(profile.id, "assigned").catch(() => []),
          getUserProducts(profile.id, "purchased").catch(() => []),
        ]);
        const hasAssignedProducts =
          Array.isArray(assignedProducts) && assignedProducts.length > 0;
        const hasPurchasedProducts =
          Array.isArray(purchasedProducts) && purchasedProducts.length > 0;
        const showPaymentLink =
          hasAssignedProducts &&
          !profile.stripeCustomerId &&
          !hasPurchasedProducts;
        setUserProfile((prev) => ({ ...prev, showPaymentLink }));
        setLoading(false);
        return;
      }

      // Fetch coach subscription (paymentsPro) and Stripe account status in parallel
      const [subscriptionRes, connectedAccount] = await Promise.all([
        getCoachSubscription().catch((err) => {
          console.error("Error fetching coach subscription:", err);
          return null;
        }),
        profile?.stripeAccountId
          ? getAccount(profile.stripeAccountId).catch((err) => {
              console.error("Error checking connected Stripe account:", err);
              return null;
            })
          : Promise.resolve(null),
      ]);

      const isPlatformOwner = profile?.id === PLATFORM_OWNER_USER_ID;
      const paymentsProEnabled = !!subscriptionRes?.paymentsPro;

      if (isPlatformOwner) {
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup: true,
          isAccountFullySetup: true,
          paymentsProEnabled: true,
        }));
        setLoading(false);
        return;
      }
      if (profile?.stripeAccountId && connectedAccount) {
        const stripeIsSetup =
          connectedAccount.charges_enabled && connectedAccount.payouts_enabled;
        const stripeDetailsSubmitted = !!connectedAccount.details_submitted;
        const stripeHasTasksDue =
          (connectedAccount?.currently_due?.length ?? 0) > 0;
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup,
          isAccountFullySetup: stripeIsSetup,
          stripeDetailsSubmitted,
          stripeHasTasksDue,
          paymentsProEnabled,
        }));
      } else {
        setUserProfile((prev) => ({
          ...prev,
          stripeIsSetup: false,
          isAccountFullySetup: false,
          stripeHasTasksDue: false,
          paymentsProEnabled,
        }));
      }
      setLoading(false);
    };
    load();
  }, [user, location?.pathname]);

  return (
    <UserInfoContext.Provider value={{ ...userProfile, loading }}>
      {children}
    </UserInfoContext.Provider>
  );
};
